import { Subscription } from 'rxjs';
import { API_BASE } from '../../../constants/general.constants';
import { Secret } from '../../../interfaces/secrets.interface';
import { ExecutionContext } from '@app/shared/interfaces/chunk/chunk-context.interface';
import {
  getSecretValueViaBridge,
  sendNotificationViaBridge,
} from './script-notification.helper';

/** Minimal shape of the connection records read from ConnectionService. */
interface ConnectionLike {
  name: string;
  type: string;
  description?: string | null;
  config?: { provider?: string | null; bucket?: string | null; prefix?: string | null };
}

/** Minimal shape of the step records read from StepService.stepList. */
interface StepLike {
  stepId: number;
  name: string;
  steptypeId: number;
  disabled: boolean;
}

/** Connection metadata exposed to step code (EHQ.listConnections / getConnection). */
interface ConnectionInfo {
  name: string;
  type: string;
  description: string | null;
}

/** Object-storage connection metadata (EHQ.listObjectStorage). */
interface ObjectStorageInfo extends ConnectionInfo {
  provider: string | null;
  bucket: string | null;
  prefix: string | null;
}

/** Step metadata exposed to step code (EHQ.listSteps / getStepInfo). */
interface StepSummary {
  stepId: number;
  name: string;
  position: number;
  stepType: string;
  disabled: boolean;
}

interface WorkflowInfo { workflowId: number; name: string | null; }
interface ProjectInfo { projectId: number; name: string | null; description: string | null; }
interface DeploymentInfo { deploymentId: string; }

/** Secret metadata (never the value) exposed to step code. */
type SecretMetadata = Pick<Secret, 'secretId' | 'name' | 'gettable' | 'sourceId' | 'sourceType' | 'sourceName'>;

/**
 * IntrospectionBridgeService — adapts Angular services for the EHQ introspection API.
 *
 * Metadata methods are synchronous to match enoch's behavior — the same Python
 * code must work identically in both browser and headless runtimes. The one
 * exception is getSecretValue (SCRUM-584), which is async because it performs an
 * audited server round-trip and is registered through the async import-bridge
 * pathway.
 *
 * Not an Angular injectable — instantiated manually in PythonService.
 */
export class IntrospectionBridgeService {
  private stepTypeCache: Map<number, string> = new Map();
  private stepTypesSubscription?: Subscription;

  // Cached project secrets METADATA (never values) for the sync listSecrets/
  // getSecret API. Populated at construction from the secrets list endpoint,
  // which returns the sanitized shape (no value). getSecretValue does NOT use
  // this cache — it always performs a fresh server round-trip + audit.
  private secretsCache: Secret[] = [];

  // The injected Angular services are kept `any` in this pass: tightening them to
  // their concrete classes needs the caller (PythonService) verified against each
  // member access and a full typecheck, so it is deferred to a focused change.
  constructor(
    private stepService: any,
    private stepTypeService: any,
    private connectionService: any,
    private appStateService: any,
    private oauthService?: any,
    private secretsService?: any,
    private pushDestinationService?: any,
    private deploymentInfoService?: any,
  ) {
    this.refreshStepTypeCache();
    this.refreshSecretsCache();
  }

  /**
   * Unsubscribe from step types; call before replacing the instance (e.g. Python init retry).
   */
  dispose(): void {
    this.stepTypesSubscription?.unsubscribe();
    this.stepTypesSubscription = undefined;
  }

  /**
   * Fetch the project's secrets metadata into the sync cache.
   *
   * The list endpoint returns the sanitized shape (no value), so caching it is
   * safe — only metadata ever lives in memory. Fired once at construction; the
   * bridge is reconstructed per project context (see PythonService.initializeEHQBridge),
   * so the cache always reflects the active project.
   */
  private refreshSecretsCache(): void {
    const projectId = this.appStateService.projectId?.() ?? this.appStateService.project?.()?.projectId;
    if (!projectId || !this.secretsService?.getSecrets) {
      return;
    }
    this.secretsService.getSecrets(projectId).subscribe({
      next: (secrets: Secret[]) => {
        // Defense-in-depth: cache metadata only, never values. Even though the
        // list endpoint returns the sanitized shape, strip `value` explicitly so
        // a value can never linger in memory if the backend regresses or a
        // different endpoint is wired in here by mistake.
        this.secretsCache = (secrets ?? []).map(
          s =>
            ({
              secretId: s.secretId,
              name: s.name,
              gettable: s.gettable,
              sourceId: s.sourceId,
              sourceType: s.sourceType,
              sourceName: s.sourceName,
            }) as Secret,
        );
      },
      error: (err: unknown) => {
        console.warn('[IntrospectionBridge] failed to load secrets metadata:', err);
        this.secretsCache = [];
      },
    });
  }

  private refreshStepTypeCache(): void {
    this.stepTypesSubscription?.unsubscribe();
    const stream = this.stepTypeService.stepTypes$;
    if (!stream?.subscribe) {
      return;
    }
    this.stepTypesSubscription = stream.subscribe((types: Array<{ steptypeId: number; name: string }>) => {
      if (types) {
        this.stepTypeCache.clear();
        for (const t of types) {
          this.stepTypeCache.set(t.steptypeId, t.name);
        }
      }
    });
  }

  private getStepTypeName(steptypeId: number): string {
    return this.stepTypeCache.get(steptypeId) || 'Unknown';
  }

  // The `args: any[]` parameter on the introspection methods below is the raw
  // argument array delivered from Python/JS step code via the bridge; its
  // contents are inherently dynamic, so it stays `any[]` (audit / ADR-0005).
  listConnections(args: any[]): ConnectionInfo[] {
    const typeFilter = args?.[0];
    // Read from the signal cache — already populated by the time
    // a user opens a project and runs a step.
    let connections = this.connectionService.connections();
    if (typeFilter) {
      connections = connections.filter((c: ConnectionLike) => c.type === typeFilter);
    }
    return connections.map((c: ConnectionLike) => ({
      name: c.name,
      type: c.type,
      description: c.description || null,
    }));
  }

  getConnection(args: any[]): ConnectionInfo | null {
    const name = args?.[0];
    const connections = this.connectionService.connections();
    const conn = connections.find((c: ConnectionLike) => c.name === name);
    if (!conn) return null;
    return {
      name: conn.name,
      type: conn.type,
      description: conn.description || null,
    };
  }

  listSteps(): StepSummary[] {
    const steps = this.stepService.stepList;
    return steps.map((s: StepLike, i: number) => ({
      stepId: s.stepId,
      name: s.name,
      position: i,
      stepType: this.getStepTypeName(s.steptypeId),
      disabled: s.disabled,
    }));
  }

  /**
   * Current step metadata. Accepts either the Python-bridge call shape
   * `(chunkContext)` or the JS import-bridge shape `(args, chunkContext, mode)`.
   */
  // `argsOrContext`/`chunkContext` are the dual-shape bridge arguments (Python
  // passes the chunk context as the first arg; the JS import bridge passes
  // `(args, chunkContext, mode)`), and the branch probes `getChunk`, which lives
  // on ChunkDataContext rather than ExecutionContext — so both stay `any`.
  getStepInfo(argsOrContext: any, chunkContext?: any): StepSummary | null {
    const ctx = chunkContext?.getChunk ? chunkContext : argsOrContext;
    const steps = this.stepService.stepList;
    if (!steps?.length) {
      return null;
    }
    const currentStepId = ctx?.getChunk?.()?.stepId
      ?? this.stepService.selectedStepId;
    const idx = steps.findIndex((s: StepLike) => s.stepId === currentStepId);
    const step = idx >= 0 ? steps[idx] : steps[0];
    const position = idx >= 0 ? idx : 0;
    return {
      stepId: step.stepId,
      name: step.name,
      position,
      stepType: this.getStepTypeName(step.steptypeId),
      disabled: step.disabled,
    };
  }

  getJobInfo(): null {
    return null;
  }

  // ==== Phase 3: Additional introspection methods ====

  getWorkflowInfo(): WorkflowInfo {
    const workflow = this.appStateService.workflow();
    return {
      workflowId: workflow?.workflowId || 0,
      name: workflow?.name || null,
    };
  }

  getProjectInfo(): ProjectInfo {
    const project = this.appStateService.project();
    return {
      projectId: project?.projectId || 0,
      name: project?.name || null,
      description: project?.description || null,
    };
  }

  /**
   * Deployment identifier for the current runtime (SCRUM-782). Mirrors enoch's
   * EHQ.getDeploymentInfo() so step code reads the deployment id portably across
   * runtimes — getJobInfo() is null in the browser, but this still resolves.
   * Sourced from the server's deployment-info (DeploymentInfoService), so a local
   * browser pointed at dev reports `dev`, not `localhost`.
   */
  getDeploymentInfo(): DeploymentInfo {
    return {
      deploymentId: this.deploymentInfoService?.deploymentId() ?? 'unknown',
    };
  }

  isFeatureEnabled(args: any[]): boolean {
    const featureName = args?.[0];
    if (!featureName) return false;

    const typeMap: Record<string, string> = {
      llm: 'LLM',
      datafusion: 'DataFusion',
      box: 'Box',
      objectstorage: 'ObjectStorage',
      database: 'Database',
    };
    const connType = typeMap[featureName.toLowerCase()];
    if (connType) {
      const connections = this.connectionService.connections();
      return connections.some((c: ConnectionLike) => c.type === connType);
    }
    return false;
  }

  listObjectStorage(): ObjectStorageInfo[] {
    const connections = this.connectionService.connections();
    return connections
      .filter((c: ConnectionLike) => c.type === 'ObjectStorage')
      .map((c: ConnectionLike) => ({
        name: c.name,
        type: c.type,
        description: c.description || null,
        provider: c.config?.provider || null,
        bucket: c.config?.bucket || null,
        prefix: c.config?.prefix || null,
      }));
  }

  // ==== Phase 2: Control flow ====

  /**
   * skipToStep — browser implementation.
   *
   * In the browser, step execution is single-step (user clicks Run on one step).
   * skipToStep logs the intent but does not alter execution — there is no
   * multi-step execution loop to skip within. The step completes normally.
   *
   * Full skip behavior is headless-only (enoch).
   */
  skipToStep(args: any[], chunkContext: ExecutionContext): void {
    const targetName = args?.[0];
    if (!targetName || typeof targetName !== 'string') {
      throw new Error('skipToStep requires a step name (string)');
    }

    const steps = this.stepService.stepList;
    const target = steps.find((s: StepLike) => s.name === targetName);
    if (!target) {
      throw new Error(`skipToStep: step "${targetName}" not found`);
    }

    chunkContext?.addLog?.(
      `skipToStep("${targetName}") — noted (browser executes steps individually)`,
      'info',
    );
  }

  // ── Enoch compatibility (SCRUM-254) ─────────────────────────────────

  /**
   * getAuthToken — return the current OAuth bearer token.
   */
  getAuthToken(): string {
    return this.oauthService?.getAccessToken?.() || '';
  }

  /**
   * getApiUrl — return the base API URL from environment config.
   *
   * Matches enoch's EHQ.getApiUrl() contract exactly: the URL is absolute and
   * returned with a single trailing slash (e.g. "http://nc-server/api/") so step
   * code can build endpoints by direct concatenation — `f"{api_url}fetch-proxy"`
   * — identically in the browser and in headless execution.
   *
   * API_BASE may be relative (e.g. "/api" in local/runtime-configured builds) or
   * already absolute (dev/stage/local-server). A relative base is resolved against
   * the page origin so the result is always absolute; enoch returns an absolute
   * URL and ehq.fetch() rejects non-http(s) URLs, so a relative value broke any
   * browser-run step that proxied a fetch via getApiUrl() (SCRUM-742). API_BASE is
   * stored without a trailing slash (see environment.*.ts), so we append one.
   */
  getApiUrl(): string {
    const base = API_BASE || '';
    if (!base) {
      return '';
    }
    let absolute = base;
    if (!/^https?:\/\//i.test(base)) {
      const path = base.startsWith('/') ? base : `/${base}`;
      absolute = `${window.location.origin}${path}`;
    }
    return absolute.endsWith('/') ? absolute : `${absolute}/`;
  }

  // ── Secrets (SCRUM-584) ─────────────────────────────────────────────
  //
  // Metadata (listSecrets/getSecret) is SYNC, served from the in-memory
  // secretsCache — values are never present in that cache. The value
  // (getSecretValue) is ASYNC: it always hits the server, which performs
  // access checks and audit logging. API contract matches enoch exactly.

  /**
   * Map a cached Secret to the metadata shape exposed to step code.
   * Strictly excludes the value.
   */
  private toSecretMetadata(s: Secret): SecretMetadata {
    return {
      secretId: s.secretId,
      name: s.name,
      gettable: s.gettable,
      sourceId: s.sourceId,
      sourceType: s.sourceType,
      sourceName: s.sourceName,
    };
  }

  /**
   * listSecrets() — return metadata for all project secrets (no values).
   */
  listSecrets(): SecretMetadata[] {
    return this.secretsCache.map(s => this.toSecretMetadata(s));
  }

  /**
   * getSecret(name) — return metadata for one secret by name, or null.
   */
  getSecret(args: any[]): SecretMetadata | null {
    const name = args?.[0];
    const secret = this.secretsCache.find(s => s.name === name);
    if (!secret) return null;
    return this.toSecretMetadata(secret);
  }

  /**
   * getSecretValue(nameOrId) — resolve a secret value via the server.
   *
   * Accepts EITHER a secret name (string → resolved to id via the metadata
   * cache) OR a secret id (number → used directly). Always performs a server
   * round-trip so the server can enforce access control and write an audit
   * record. Async: invoked through the import (async) bridge pathway.
   *
   * Signature matches the import-bridge contract: (args, chunkContext, mode).
   */
  async getSecretValue(args: any[]): Promise<string> {
    return getSecretValueViaBridge(
      args,
      this.appStateService,
      this.secretsService,
      name => this.secretsCache.find(s => s.name === name),
    );
  }

  /**
   * sendNotification(destination, message, options?) — send a script-triggered
   * ("Manual") notification to a configured push destination. Forwards to the
   * server's POST /push-destinations/send; delivery is asynchronous and
   * rate-limited server-side. Async: invoked through the import bridge pathway.
   */
  async sendNotification(args: any[]): Promise<boolean> {
    return sendNotificationViaBridge(
      args,
      this.appStateService,
      this.pushDestinationService,
    );
  }
}
